import { useState } from 'react';

interface NumberFieldProps {
    label: string;
    value: number | null;
    onCommit: (value: number | null) => void;
    /** Allows clearing the field, committing `null` (e.g. "use storey height"). */
    allowEmpty?: boolean;
    placeholder?: string;
    step?: number;
    min?: number;
    disabled?: boolean;
    testId?: string;
}

/** Metre input that commits on Enter or blur, so each edit is one undo step and partial input is never applied. */
export const NumberField = ({
    label,
    value,
    onCommit,
    allowEmpty = false,
    placeholder,
    step = 0.01,
    min,
    disabled,
    testId
}: NumberFieldProps) => {
    const [draft, setDraft] = useState<string | null>(null);
    const shown = draft ?? (value === null ? '' : String(value));

    const commit = () => {
        const current = draft;
        setDraft(null);
        if (current === null) {
            return;
        }
        const normalized = current.trim().replace(',', '.');
        if (normalized === '') {
            if (allowEmpty && value !== null) {
                onCommit(null);
            }
            return;
        }
        const parsed = Number(normalized);
        if (!Number.isFinite(parsed) || (min !== undefined && parsed < min)) {
            return;
        }
        const rounded = Math.round(parsed * 1000) / 1000;
        if (rounded !== value) {
            onCommit(rounded);
        }
    };

    return (
        <label className="block min-w-0 text-xs text-text-light">
            {label}
            <input
                aria-label={label}
                className="mt-0.5 h-7 w-full rounded-sm border border-border-light px-1.5 text-sm text-text-default outline-none focus:border-border-accent"
                data-testid={testId}
                disabled={disabled}
                inputMode="decimal"
                min={min}
                placeholder={placeholder}
                step={step}
                type="number"
                value={shown}
                onBlur={commit}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        commit();
                    } else if (event.key === 'Escape') {
                        setDraft(null);
                    }
                }}
            />
        </label>
    );
};
